/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type CSSProperties, type ReactElement } from 'react';
import { TONE_TEXT, type RipperPaperTone } from './tone-text';
// Raw `/images/...` paths work in dev but 404 on Managed Runtime — import as Vite assets.
// Final "paper rip" art: a flat sheet with a torn bottom edge, transparent below the tear. Four
// variants per tone — desktop/mobile art (different aspect ratios, not just scales) × 1x/2x density.
import blackDesktop1x from '/images/ripper-paper/paper-rip-black-desktop-1x.webp';
import blackDesktop2x from '/images/ripper-paper/paper-rip-black-desktop-2x.webp';
import blackMobile1x from '/images/ripper-paper/paper-rip-black-mobile-1x.webp';
import blackMobile2x from '/images/ripper-paper/paper-rip-black-mobile-2x.webp';
import whiteDesktop1x from '/images/ripper-paper/paper-rip-white-desktop-1x.webp';
import whiteDesktop2x from '/images/ripper-paper/paper-rip-white-desktop-2x.webp';
import whiteMobile1x from '/images/ripper-paper/paper-rip-white-mobile-1x.webp';
import whiteMobile2x from '/images/ripper-paper/paper-rip-white-mobile-2x.webp';

/** Merchandiser-facing tone. "none" removes the paper and hands the look to Default Settings. */
export type RipperTone = RipperPaperTone | 'none';

/**
 * The tones this module accepts, in Page Designer editor order.
 *
 * Deliberately NOT exported for the `@AttributeDefinition` decorators to spread: `generate-cartridge`
 * serialises those decorators from source without evaluating them, so `values: [...RIPPER_TONES]`
 * emits the literal string `"...RIPPER_TONES"` as the enum's only value — which unit tests do not
 * catch, because they import the module and evaluate it at runtime. The decorators keep inline
 * literals and are kept in sync with this list by hand.
 */
const RIPPER_TONES = ['dark', 'light', 'none'] as const;

/**
 * Artwork per tone. The design ships the two tones as separate files — `white` is the light sheet
 * (#FEFDFB), `black` the dark one (#0F0C00) — so there is no CSS `invert` trick any more.
 */
const ASSET: Record<RipperPaperTone, { desktop: string[]; mobile: string[] }> = {
    light: { desktop: [whiteDesktop1x, whiteDesktop2x], mobile: [whiteMobile1x, whiteMobile2x] },
    dark: { desktop: [blackDesktop1x, blackDesktop2x], mobile: [blackMobile1x, blackMobile2x] },
};

/** `md` (768px) — the same breakpoint the Article Hero uses to switch its own mobile/desktop art. */
const MOBILE_MEDIA = '(max-width: 767px)';

const srcSet = ([x1, x2]: string[]): string => `${x1} 1x, ${x2} 2x`;

export interface RipperBackdrop {
    /** True for tone "none": no paper, and Default Settings owns background + text colour. */
    noPaper: boolean;
    /** Spread on the content wrapper — text colour that reads on the sheet. Undefined when "none". */
    contentStyle?: CSSProperties;
    /** Full-bleed paper layer, or null when "none". Its host must be positioned + `overflow-hidden`. */
    paper: ReactElement | null;
}

/**
 * Resolve a Page Designer "Ripper Paper" tone into the pieces a section needs to render the torn
 * paper motif: the full-bleed paper layer plus the text colour that reads on it.
 *
 * The content that should sit ON the paper must be wrapped in a positioned element carrying
 * `contentStyle` — the paper layer is absolutely positioned, so unpositioned siblings paint under it.
 *
 * The art is **transparent below the tear**, so whatever is behind the section shows through there —
 * normally the page ground. The section therefore gets no background of its own; painting one would
 * fill the torn-away area and hide the tear.
 *
 * @example
 *   const { noPaper, contentStyle, paper } = ripperBackdrop(ripperTone, 'none');
 *   <section className={cn(!noPaper && 'relative overflow-hidden')}>
 *       {paper}
 *       <div className={cn(!noPaper && 'relative')} style={contentStyle}>…</div>
 *   </section>
 */
export function ripperBackdrop(ripperTone: string | undefined, fallback: RipperTone): RipperBackdrop {
    const tone: RipperTone = (RIPPER_TONES as readonly string[]).includes(ripperTone as RipperTone)
        ? (ripperTone as RipperTone)
        : fallback;

    if (tone === 'none') return { noPaper: true, paper: null };

    const art = ASSET[tone];
    return {
        noPaper: false,
        // The sheet colour is baked into the art, so the tone's only remaining CSS effect is the
        // text that sits on it — the shared map in ./tone-text (ink on light, organic white on dark).
        contentStyle: { color: TONE_TEXT[tone] },
        // Stretched to the section box (not aspect-preserved) so the torn edge always lands at the
        // section's base. The sheet is a flat colour, so vertical stretch is invisible — the only
        // effect is that the tear's thickness scales with the section's height.
        //
        // `contents` on the <picture>: the <img> is absolutely positioned, but <picture> itself is an
        // in-flow inline element, so without it the wrapper could contribute a stray line box to the
        // section. Source selection is resolved from the DOM and is unaffected by `display`.
        paper: (
            <picture className="contents" data-slot="ripper-paper" data-tone={tone}>
                <source media={MOBILE_MEDIA} srcSet={srcSet(art.mobile)} type="image/webp" />
                <source srcSet={srcSet(art.desktop)} type="image/webp" />
                <img
                    src={art.desktop[0]}
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 h-full w-full"
                />
            </picture>
        ),
    };
}
